<script setup lang="ts">
type Props = {
  opacity?: boolean;
};

const { opacity = false } = defineProps<Props>();

const { openToaster } = useLayoutStore();
const { t } = useTexts();
const { userAcceptsMarketing, fetchUserProfile } = useUserData();

const isLoading = ref(false);
const isMarketingActive = ref(false);
const isMounted = ref(false);

async function onCheckboxChanges() {
  if (!isMounted.value) return;

  let payload: null | string;

  if (!isMarketingActive.value) {
    payload = null;
  } else {
    payload = new Date().toISOString();
  }

  isLoading.value = true;

  const { ApiService } = useApiService();

  await ApiService.patch(`bff/users/self-mkt`, {
    mkt_accepted_at: payload,
  })
    .then(async () => {
      const message = isMarketingActive.value
        ? t('account.updates_section.enable_success')
        : t('account.updates_section.disable_success');

      const type = isMarketingActive.value ? 'success' : 'warning';

      openToaster({
        message,
        type,
      });
      await fetchUserProfile({ force: true });
    })
    .catch((err) => {
      const message =
        err.data?.message || t('account.updates_section.update_error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

watch(() => isMarketingActive.value, onCheckboxChanges);

onMounted(async () => {
  if (userAcceptsMarketing.value) {
    isMarketingActive.value = true;
  }
  await nextTick();
  isMounted.value = true;
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute !z-20"
      :icon-loader="true"
      :show="isLoading"
    />
    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <div :class="$style.header">
        <p :class="$style.title"> {{ $t('account.updates_section.title') }} </p>

        <BaseFormToggle
          v-model="isMarketingActive"
          :class="$style.switch"
        />
      </div>
      <div :class="$style.viewContent">
        <p :class="$style.text"> {{ $t('account.updates_section.text') }} </p>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-1 p-1 md:p-0 px-1 md:px-2;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-start justify-between gap-4;

    .title {
      @apply text-xs sm:text-sm font-semibold flex items-center gap-2;
    }

    .switch {
      @apply text-xs font-bold uppercase px-1;
    }
  }

  .viewContent {
    @apply text-base text-texts/80;

    .text {
      @apply text-xs;
    }
  }
}
</style>
